import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import BaseSpinner from "components/BaseSpinner";
import ContentHeader from "components/ContentHeader";
import ModalWindow from "components/ModalWindow";
import { Routes } from "../../router/config";
import "./style.scss";

interface Props {
  show: boolean;
  onClose: () => void;
}

export default function NewChatModal({ show, onClose }: Props) {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<UserPublicData[]>([]);
  const [loading, setLoading] = useState(false);

  const searchUsers = useCallback(
    async (q: string) => {
      if (!q.trim() || !user) {
        setUsers([]);
        return;
      }
      setLoading(true);
      try {
        const response = await providers.UserPublicDataProvider.search({
          data: { relevantTerm: q, size: 20 },
        });
        setUsers(response.result.filter((u: UserPublicData) => u.id !== user.id));
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchUsers]);

  useEffect(() => {
    if (!show) {
      setQuery("");
      setUsers([]);
    }
  }, [show]);

  const onSelectUser = (selectedUser: UserPublicData) => {
    onClose();
    history.push(`${Routes.messages}/${selectedUser.id}`);
  };

  return (
    <ModalWindow className="new-chat-modal" show={show} onClose={onClose}>
      <ContentHeader className="mb-4">{t("New chat")}</ContentHeader>
      <div className="new-chat-modal__search">
        <svg className="new-chat-modal__search-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
        <input
          type="text"
          placeholder={t("Search users...")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      <div className="new-chat-modal__list">
        {loading && (
          <div className="new-chat-modal__loading">
            <BaseSpinner />
          </div>
        )}
        {!loading && users.length === 0 && query.trim() && (
          <div className="new-chat-modal__empty">{t("No users found")}</div>
        )}
        {!loading &&
          users.map((u) => (
            <button
              key={u.id}
              className="new-chat-modal__user-item"
              onClick={() => onSelectUser(u)}
              type="button"
            >
              <BaseAvatar user={u} size={40} className="new-chat-modal__user-avatar" />
              <div className="new-chat-modal__user-info">
                <span className="new-chat-modal__user-name">{u.screenName}</span>
                {u.username && <span className="new-chat-modal__user-username">@{u.username}</span>}
              </div>
            </button>
          ))}
      </div>
    </ModalWindow>
  );
}
